const AVAILABILITY_API_BASE_URL = 'https://nordik-usa-shipping-rules.vercel.app'
const AVAILABILITY_RESOLVED_POSTAL_SESSION_KEY = 'nordikUsaResolvedPostal'

class AvailabilityWidget extends HTMLElement {
  connectedCallback() {
    this.requestGen = 0
    this.inStockBound = false
    this.preorderBound = false

    if (this.getAttribute('data-state') === 'out-of-stock') return

    this.resolveAvailability()
  }

  // ---------- Shared helpers ----------
  apiUrl(params) {
    const variantId = this.getAttribute('data-variant-id')
    return (
      AVAILABILITY_API_BASE_URL +
      '/api/rates?' +
      params +
      (variantId ? '&variant_id=' + encodeURIComponent(variantId) : '')
    )
  }

  fetchRates(params) {
    return fetch(this.apiUrl(params)).then((res) => {
      if (!res.ok) throw new Error('Request failed')
      return res.json()
    })
  }

  startRequest() {
    this.requestGen += 1
    return this.requestGen
  }

  isStale(gen) {
    return gen !== this.requestGen
  }

  // US ZIP codes are strictly numeric — 5 digits, optionally a "-XXXX"
  // extension.
  isValidZipFormat(value) {
    return /^\d{5}(-\d{4})?$/.test(value.trim())
  }

  // ---------- Resolve + state switching ----------
  resolveAvailability() {
    const cachedPostal = window.sessionStorage.getItem(
      AVAILABILITY_RESOLVED_POSTAL_SESSION_KEY,
    )
    const gen = this.startRequest()

    const lookup = cachedPostal
      ? this.fetchRates('postal=' + encodeURIComponent(cachedPostal))
      : this.fetchRates('auto=true').then((data) => {
          if (data.detectedAutomatically && data.postalValid && data.postal) {
            window.sessionStorage.setItem(
              AVAILABILITY_RESOLVED_POSTAL_SESSION_KEY,
              data.postal,
            )
          }
          return data
        })

    lookup
      .then((data) => {
        if (this.isStale(gen)) return
        this.applyState(data)
      })
      .catch(() => {
        if (this.isStale(gen)) return
        this.setAttribute('data-state', 'in-stock')
        this.showInStock({ skipAutoDetect: true })
      })
  }

  applyState(data) {
    if (!data.postalValid || !data.postal) {
      this.setAttribute('data-state', 'in-stock')
      this.showInStock({ skipAutoDetect: true })
      return
    }
    if (data.isPreOrder) {
      this.setAttribute('data-state', 'preorder')
      this.showPreorder({ initialData: data })
    } else {
      this.setAttribute('data-state', 'in-stock')
      this.showInStock({ initialData: data })
    }
  }

  // ---------- In stock ----------
  showInStock(opts) {
    opts = opts || {}
    const editBtn = this.querySelector('[data-action="edit-postal"]')
    const form = this.querySelector('[data-form="postal"]')
    const input = this.querySelector('[data-input="postal"]')
    const error = this.querySelector('[data-error="postal"]')

    if (!this.inStockBound) {
      this.inStockBound = true
      editBtn.addEventListener('click', () => {
        error.hidden = true
        input.value = ''
        form.hidden = false
        input.focus()
      })
      form.addEventListener('submit', (event) => {
        event.preventDefault()
        const postal = input.value.trim()
        if (!postal) return
        if (!this.isValidZipFormat(postal)) {
          error.hidden = false
          return
        }
        this.lookupInStockManual(postal)
      })
    }

    if (opts.initialData) {
      this.applyInStockResult(opts.initialData)
      form.hidden = true
      input.value = ''
      error.hidden = true
    } else {
      form.hidden = false
    }
  }

  applyInStockResult(data) {
    const deliveryText = this.querySelector('[data-text="delivery-line"]')
    const locationText = this.querySelector('[data-text="location-line"]')
    const editBtn = this.querySelector('[data-action="edit-postal"]')
    const error = this.querySelector('[data-error="postal"]')

    if (data.postalValid === false) {
      error.hidden = false
      return
    }
    error.hidden = true
    const rate = data.rates && data.rates[0]
    deliveryText.textContent =
      rate && rate.etaText ? rate.etaText : this.dataset.deliveryPrompt
    locationText.textContent = data.postal
      ? this.dataset.deliveringToPrefix + data.postal
      : this.dataset.locationPrompt
    editBtn.hidden = false
  }

  lookupInStockManual(postal) {
    const form = this.querySelector('[data-form="postal"]')
    const error = this.querySelector('[data-error="postal"]')
    const gen = this.startRequest()

    this.fetchRates('postal=' + encodeURIComponent(postal))
      .then((data) => {
        if (this.isStale(gen)) return
        if (data.postalValid === false) {
          error.hidden = false
          return
        }
        window.sessionStorage.setItem(
          AVAILABILITY_RESOLVED_POSTAL_SESSION_KEY,
          postal,
        )
        if (data.isPreOrder) {
          this.setAttribute('data-state', 'preorder')
          this.showPreorder({ initialData: data })
          return
        }
        error.hidden = true
        this.applyInStockResult(data)
        form.hidden = true
      })
      .catch(() => {
        if (this.isStale(gen)) return
        error.hidden = false
      })
  }

  // ---------- Pre-order ----------
  showPreorder(opts) {
    opts = opts || {}
    const form = this.querySelector('[data-form="preorder-postal"]')
    const input = this.querySelector('[data-input="preorder-postal"]')
    const error = this.querySelector('[data-error="preorder-postal"]')
    const editBtn = this.querySelector('[data-action="preorder-edit-postal"]')

    if (!this.preorderBound) {
      this.preorderBound = true
      editBtn.addEventListener('click', () => {
        error.hidden = true
        input.value = ''
        form.hidden = false
        input.focus()
      })
      form.addEventListener('submit', (event) => {
        event.preventDefault()
        const postal = input.value.trim()
        if (!postal) return
        if (!this.isValidZipFormat(postal)) {
          error.hidden = false
          return
        }
        this.lookupPreorderManual(postal)
      })
    }

    if (opts.initialData) {
      this.applyPreorderResult(opts.initialData)
      form.hidden = true
      input.value = ''
      error.hidden = true
    } else {
      form.hidden = false
    }
  }

  applyPreorderResult(data) {
    const shippingText = this.querySelector(
      '[data-text="preorder-shipping-line"]',
    )
    const preorderText = this.querySelector('[data-text="preorder-line"]')
    const locationText = this.querySelector(
      '[data-text="preorder-location-line"]',
    )
    const editBtn = this.querySelector('[data-action="preorder-edit-postal"]')
    const error = this.querySelector('[data-error="preorder-postal"]')

    if (data.postalValid === false) {
      error.hidden = false
      return
    }
    error.hidden = true
    const rate = data.rates && data.rates[0]
    if (data.warehouse) {
      shippingText.textContent =
        'FREE Shipping from ' + data.warehouse + ', ' + data.warehouseState
    }
    if (rate && rate.etaText) {
      preorderText.textContent = rate.etaText
    }
    locationText.textContent = data.postal
      ? this.dataset.estimatedForPrefix + data.postal
      : ''
    editBtn.hidden = false
  }

  lookupPreorderManual(postal) {
    const form = this.querySelector('[data-form="preorder-postal"]')
    const error = this.querySelector('[data-error="preorder-postal"]')
    const gen = this.startRequest()

    this.fetchRates('postal=' + encodeURIComponent(postal))
      .then((data) => {
        if (this.isStale(gen)) return
        if (data.postalValid === false) {
          error.hidden = false
          return
        }
        window.sessionStorage.setItem(
          AVAILABILITY_RESOLVED_POSTAL_SESSION_KEY,
          postal,
        )
        if (!data.isPreOrder) {
          this.setAttribute('data-state', 'in-stock')
          this.showInStock({ initialData: data })
          return
        }
        error.hidden = true
        this.applyPreorderResult(data)
        form.hidden = true
      })
      .catch(() => {
        if (this.isStale(gen)) return
        error.hidden = false
      })
  }
}

customElements.define('shipping-availability', AvailabilityWidget)
